import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  DayPicker,
  getDefaultClassNames,
  type DayButton,
} from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import { cn } from "@/lib/utils";

/** Applied on `.rdp-root` so react-day-picker global CSS uses compact spacing. */
const calendarSizeVars = [
  "[--rdp-nav-height:var(--cell-size)]",
  "[--rdp-nav_button-height:var(--cell-size)]",
  "[--rdp-nav_button-width:var(--cell-size)]",
  "[--rdp-day-height:var(--cell-size)]",
  "[--rdp-day-width:var(--cell-size)]",
  "[--rdp-day_button-height:var(--cell-size)]",
  "[--rdp-day_button-width:var(--cell-size)]",
  "[--rdp-weekday-padding:0]",
  "[--rdp-day_button-border:0]",
  "[--rdp-months-gap:0]",
].join(" ");

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /** @default "default" */
  size?: "default" | "compact";
};

const sizePresets = {
  compact: {
    cell: "1.75rem",
    padding: "px-2 py-1.5",
    caption: "text-xs min-w-[7rem]",
    weekday: "text-xs",
    day: "text-xs leading-none",
  },
  default: {
    cell: "2.25rem",
    padding: "p-3",
    caption: "text-sm min-w-[8rem]",
    weekday: "text-xs",
    day: "text-sm leading-none",
  },
} as const;

function createCalendarDayButton(dayClassName: string) {
  function CalendarDayButton({
    className,
    day,
    modifiers,
    ...props
  }: React.ComponentProps<typeof DayButton>) {
    const ref = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
      if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    return (
      <button
        ref={ref}
        type="button"
        data-day={day.date.toLocaleDateString("pt-BR")}
        data-selected-single={
          modifiers.selected &&
          !modifiers.range_start &&
          !modifiers.range_end &&
          !modifiers.range_middle
        }
        data-range-start={modifiers.range_start}
        data-range-end={modifiers.range_end}
        data-range-middle={modifiers.range_middle}
        className={cn(
          "inline-flex size-(--cell-size) cursor-pointer items-center justify-center rounded-none border-0 bg-transparent p-0 font-light",
          dayClassName,
          "text-primary-900 hover:bg-primary-100 dark:text-white dark:hover:bg-primary-800",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400",
          "data-[selected-single=true]:bg-primary-950 data-[selected-single=true]:text-white",
          "dark:data-[selected-single=true]:bg-white dark:data-[selected-single=true]:text-primary-950",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    );
  }

  return CalendarDayButton;
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  captionLayout = "label",
  locale = ptBR,
  formatters,
  components,
  size = "default",
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames();
  const preset = sizePresets[size];
  const cellSizeVar = `[--cell-size:${preset.cell}]`;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      locale={locale}
      captionLayout={captionLayout}
      className={cn(
        "group/calendar bg-white dark:bg-primary-950",
        preset.padding,
        cellSizeVar,
        className
      )}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("pt-BR", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", cellSizeVar, calendarSizeVars, defaultClassNames.root),
        months: cn("relative flex flex-col gap-0", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-0", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex h-(--cell-size) w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          "inline-flex size-(--cell-size) shrink-0 cursor-pointer items-center justify-center rounded-none border-0 bg-transparent p-0",
          "text-primary-700 hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-800",
          "select-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400",
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          "inline-flex size-(--cell-size) shrink-0 cursor-pointer items-center justify-center rounded-none border-0 bg-transparent p-0",
          "text-primary-700 hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-800",
          "select-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400",
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "mb-1 flex h-(--cell-size) w-full shrink-0 items-center justify-center px-(--cell-size) text-xs font-light",
          defaultClassNames.month_caption
        ),
        caption_label: cn(
          "text-center font-light tracking-wide select-none text-primary-900 dark:text-white",
          preset.caption,
          defaultClassNames.caption_label
        ),
        month_grid: cn("w-full font-light", preset.day, defaultClassNames.month_grid),
        weekdays: cn("flex w-full", defaultClassNames.weekdays),
        weekday: cn(
          "flex flex-1 items-center justify-center p-0 font-light text-primary-500 select-none dark:text-primary-400",
          preset.weekday,
          defaultClassNames.weekday
        ),
        weeks: cn("flex w-full flex-col gap-0", defaultClassNames.weeks),
        week: cn("mt-0 flex w-full", defaultClassNames.week),
        day: cn(
          "relative flex flex-1 items-center justify-center p-0 text-center select-none",
          defaultClassNames.day
        ),
        day_button: cn("size-(--cell-size) shrink-0 p-0 font-light", preset.day, defaultClassNames.day_button),
        selected: cn("font-light", preset.day, defaultClassNames.selected),
        today: cn(
          "rounded-none bg-primary-100 text-primary-950 dark:bg-primary-800 dark:text-white",
          defaultClassNames.today
        ),
        outside: cn(
          "text-primary-400 aria-selected:text-primary-400",
          defaultClassNames.outside
        ),
        disabled: cn("text-primary-300 opacity-50", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            ref={rootRef}
            className={cn(rootClassName)}
            {...rootProps}
          />
        ),
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return (
            <Icon
              className={cn(size === "compact" ? "size-3" : "size-4", "shrink-0", chevronClassName)}
              {...chevronProps}
            />
          );
        },
        DayButton: createCalendarDayButton(preset.day),
        ...components,
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar, createCalendarDayButton };
